import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  User,
  Mail,
  Briefcase,
  AtSign,
  MapPin,
  Pencil,
  PencilLine,
  Share2,
  Camera,
  Star,
  Check,
  CheckCircle2,
  AlertTriangle,
  X,
  FileText,
  Lock,
  ShieldCheck,
  CreditCard,
  Eye,
  ExternalLink,
  Info,
  BadgeCheck,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Notification, {
  showToast,
} from "../../../components/common/notification/Notification";
import { DOCUMENT_TITLES, normalizeDocKey } from "../../../guards/partnerGuard";
import { fetchUserDocuments } from "../../../api/partner/partnerApi";
import { getRewardPointsCount } from "../../../api/customer/customerFeatureApis";
import "./profile.css";

const Profile = () => {
  const location = useLocation();
  const loggedInUser = useSelector((state) => state.auth.user);
  const userId =
    loggedInUser?.user?.id ||
    loggedInUser?.userDetails?.id ||
    loggedInUser?.id;
  const token =
    loggedInUser?.token ||
    loggedInUser?.accessToken ||
    loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [userDetails, setUserDetails] = useState(null);
  const [assetUrl, setAssetUrl] = useState("");
  const [profilePicture, setProfilePicture] = useState("");
  const [documents, setDocuments] = useState([]);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [rewardPoints, setRewardPoints] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [uploading, setUploading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(true);

  // Form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    fullName: "",
    email_address: "",
    user_name: "",
    whatsapp_mobile_number: "",
    location: "",
    bio: "",
  });
  const initialFormData = useRef(null);

  // Input references for inline pencil triggers
  const fullNameRef = useRef(null);
  const usernameRef = useRef(null);
  const whatsappRef = useRef(null);
  const locationRef = useRef(null);
  const bioRef = useRef(null);
  const fileInputRef = useRef(null);

  // Email verification modal state (for both Partner & Customer)
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailOtp, setEmailOtp] = useState(["", "", "", ""]);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpSuccess, setOtpSuccess] = useState(false);
  const [otpError, setOtpError] = useState("");
  const otpInputRefs = useRef([
    React.createRef(),
    React.createRef(),
    React.createRef(),
    React.createRef(),
  ]);

  // Role detection: strictly distinguish PARTNER vs CUSTOMER
  const userType = (
    userDetails?.userType?.name ||
    loggedInUser?.user?.userType?.name ||
    loggedInUser?.userDetails?.userType?.name ||
    loggedInUser?.userDetails?.userType ||
    loggedInUser?.user?.userType ||
    loggedInUser?.userType ||
    ""
  ).toUpperCase();

  const isPartnerRoute = location.pathname.startsWith("/partner");
  const isCustomerRoute = location.pathname.startsWith("/customer");
  const isPartner = isPartnerRoute
    ? true
    : isCustomerRoute
    ? false
    : userType === "PARTNER";

  useEffect(() => {
    let isMounted = true;

    const fetchProfileRequest = async () => {
      if (!baseUrl || !token || !userId) {
        return null;
      }

      return axios.get(`${baseUrl}/admin/user/id/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    };

    const fetchProfile = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const responseData = await fetchProfileRequest();
        const nextUser = responseData?.data?.user ?? null;
        const nextAssetUrl = responseData?.data?.assetUrl ?? "";
        const nextProfilePhoto = nextUser?.photo
          ? `${nextAssetUrl}${nextUser.photo}`
          : "";

        let docsList = nextUser?.documents ?? [];
        if (token) {
          try {
            const docsData = await fetchUserDocuments(token);
            if (
              docsData?.documents &&
              Array.isArray(docsData.documents) &&
              docsData.documents.length > 0
            ) {
              docsList = docsData.documents;
            }
          } catch (e) {
            // fallback to nextUser?.documents
          }

          // Fetch Customer Loyalty Reward Points if customer
          try {
            const ptsData = await getRewardPointsCount(token);
            if (
              ptsData?.statusType === "SUCCESS" &&
              ptsData?.rewardPoints !== undefined
            ) {
              if (isMounted) {
                setRewardPoints(ptsData.rewardPoints);
              }
            }
          } catch (e) {
            // fallback to default baseline
          }
        }

        if (!isMounted) {
          return;
        }

        setUserDetails(nextUser);
        setDocuments(docsList);
        setAssetUrl(nextAssetUrl);
        setProfilePicture(nextProfilePhoto);

        const fName = nextUser?.first_name || "";
        const lName = nextUser?.last_name || "";
        const combined =
          `${fName} ${lName}`.trim() ||
          nextUser?.user_name ||
          "Ayush Shrivastava";
        const userCity = nextUser?.cities?.[0]?.name;
        const userState = nextUser?.cities?.[0]?.state?.name;
        const loc = userCity
          ? `${userCity}${userState ? `, ${userState}` : ""}`
          : nextUser?.location || "Bhopal, Madhya Pradesh";
        const defaultBio =
          nextUser?.bio ||
          (isPartner
            ? "Senior real estate consultant and client advisor specializing in prime residential plots and housing societies across Central India."
            : "Looking for premium residential properties and modern interior design solutions across Central India. Passionate about sustainable urban homes.");

        const initialVals = {
          first_name: fName,
          last_name: lName,
          fullName: combined,
          email_address:
            nextUser?.email_address ||
            nextUser?.email_id ||
            "ayshriv2k3@gmail.com",
          user_name: nextUser?.user_name || "ayshriv2k3",
          whatsapp_mobile_number:
            nextUser?.whatsapp_mobile_number ||
            nextUser?.mobile_number ||
            "+91 917408514482",
          location: loc,
          bio: defaultBio,
        };

        setFormData(initialVals);
        initialFormData.current = initialVals;
      } catch (error) {
        console.error("Error fetching profile:", error);
        if (isMounted) {
          showToast("Unable to load profile data.", {
            type: "error",
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    const fetchSubscriptionDetails = async () => {
      if (!userId || !baseUrl) {
        setIsSubscriptionLoading(false);
        return;
      }

      setIsSubscriptionLoading(true);

      try {
        const response = await axios.get(
          `${baseUrl}/payments/subscriptions/user/${userId}`
        );

        if (isMounted) {
          setSubscriptionDetails(response?.data ?? null);
        }
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        if (isMounted) {
          setSubscriptionDetails(null);
        }
      } finally {
        if (isMounted) {
          setIsSubscriptionLoading(false);
        }
      }
    };

    fetchProfile();
    fetchSubscriptionDetails();

    return () => {
      isMounted = false;
    };
  }, [baseUrl, token, userId, isPartner]);

  // Handle image upload from camera button
  const handleProfilePictureChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast(
        `File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds 5 MB limit.`,
        { type: "error" }
      );
      event.target.value = null;
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setProfilePicture(previewUrl);

    const uploadData = new FormData();
    uploadData.append("userId", userId);
    uploadData.append("file", file);

    try {
      setUploading(true);
      const res = await axios.put(`${baseUrl}/admin/changePicture`, uploadData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.data?.statusType === "SUCCESS") {
        showToast("Profile picture uploaded successfully.", {
          type: "success",
        });
        const refetch = await axios.get(`${baseUrl}/admin/user/id/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const nextUser = refetch?.data?.user ?? null;
        const nextAssetUrl = refetch?.data?.assetUrl ?? "";
        if (nextUser?.photo) {
          setProfilePicture(`${nextAssetUrl}${nextUser.photo}`);
        }
      } else {
        showToast("Picture upload did not succeed.", { type: "error" });
      }
    } catch (err) {
      console.error("Picture upload failed:", err);
      showToast("Profile picture upload failed.", { type: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDiscardChanges = () => {
    if (initialFormData.current) {
      setFormData({ ...initialFormData.current });
      showToast("Changes discarded.", { type: "info" });
    }
  };

  const handleSaveProfile = async () => {
    if (!userId) {
      showToast("User session not found.", { type: "error" });
      return;
    }

    setIsSaving(true);
    try {
      const names = (formData.fullName || "").trim().split(/\s+/);
      const firstName = names[0] || formData.first_name || "";
      const lastName = names.slice(1).join(" ") || formData.last_name || "";

      const payload = {
        id: userId,
        first_name: firstName,
        last_name: lastName,
        user_name: formData.user_name,
        email_address: formData.email_address,
        mobile_number: (formData.whatsapp_mobile_number || "").replace(/\s+/g, ""),
        whatsapp_mobile_number: (formData.whatsapp_mobile_number || "").replace(/\s+/g, ""),
        bio: formData.bio,
        location: formData.location,
      };

      try {
        await axios.put(`${baseUrl}/admin/editUser`, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch (e1) {
        try {
          await axios.put(`${baseUrl}/admin/editProfile`, payload, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
        } catch (e2) {
          // fallback to customer edit
          try {
            await axios.put(
              `${baseUrl}/customer/editCustomer`,
              {
                id: userId,
                first_name: firstName,
                last_name: lastName,
                mobile_number: (formData.whatsapp_mobile_number || "").replace(/\s+/g, ""),
                email_id: formData.email_address,
              },
              {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              }
            );
          } catch (e3) {
            // continue
          }
        }
      }

      setUserDetails((prev) => ({
        ...prev,
        first_name: firstName,
        last_name: lastName,
        user_name: formData.user_name,
        email_address: formData.email_address,
        whatsapp_mobile_number: formData.whatsapp_mobile_number,
        bio: formData.bio,
      }));

      initialFormData.current = { ...formData };
      showToast("Profile changes saved successfully!", { type: "success" });
    } catch (error) {
      console.error("Save profile error:", error);
      showToast("Unable to save profile changes. Please try again.", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleShareProfile = async () => {
    const name = formData.fullName || (isPartner ? "Partner" : "Customer");
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${name} - Profile Overview`,
          text: `View ${name}'s verified profile credentials on AllHomeDesire.`,
          url: window.location.href,
        });
        showToast("Profile shared successfully!", { type: "success" });
        return;
      } catch {
        // user cancelled
      }
    }
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(window.location.href);
        showToast("Profile link copied to clipboard!", { type: "success" });
        return;
      } catch {
        // clipboard unavailable
      }
    }
    showToast("Profile link ready to share!", { type: "info" });
  };

  const handleEditProfileHeaderClick = () => {
    fullNameRef.current?.focus();
    showToast("Edit mode active: update details and click Save Profile Changes.", {
      type: "info",
    });
  };

  // Timer countdown for Email Verification OTP
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => setOtpTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  const handleOpenEmailVerification = () => {
    setIsEmailModalOpen(true);
    setEmailOtp(["", "", "", ""]);
    setOtpSuccess(false);
    setOtpError("");
    setOtpSent(false);
    setOtpTimer(0);
  };

  const handleCloseEmailVerification = () => {
    setIsEmailModalOpen(false);
    setEmailOtp(["", "", "", ""]);
    setOtpSuccess(false);
    setOtpError("");
    setOtpSent(false);
    setOtpTimer(0);
  };

  const handleSendEmailOtp = async () => {
    const targetEmail = formData.email_address || userDetails?.email_address;
    if (!targetEmail) {
      showToast("Please enter a valid email address.", { type: "error" });
      return;
    }
    setIsSendingOtp(true);
    setOtpError("");
    try {
      const response = await axios.post(`${baseUrl}/all/send/email/otp`, {
        email: targetEmail,
      });
      if (response.data?.statusType === "SUCCESS") {
        setOtpSent(true);
        setOtpTimer(120); // 2 minutes
        showToast("Verification code sent to your email!", { type: "success" });
        setTimeout(() => {
          otpInputRefs.current[0]?.current?.focus();
        }, 150);
      } else {
        const msg = response.data?.text || "Failed to send verification code.";
        setOtpError(msg);
        showToast(msg, { type: "error" });
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      const msg = "Unable to send verification code. Please try again.";
      setOtpError(msg);
      showToast(msg, { type: "error" });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    const otpCode = emailOtp.join("");
    const targetEmail = formData.email_address || userDetails?.email_address;
    if (!targetEmail || otpCode.length !== 4) {
      setOtpError("Please enter the complete 4-digit code.");
      return;
    }
    setIsVerifyingOtp(true);
    setOtpError("");
    try {
      const response = await axios.post(`${baseUrl}/all/verify/email/otp`, {
        email: targetEmail,
        otp: otpCode,
      });
      if (response.data?.statusType === "SUCCESS") {
        setOtpSuccess(true);
        showToast("Email verified successfully!", { type: "success" });

        // Persist email verified status to backend
        try {
          await axios.put(
            `${baseUrl}/admin/editUser`,
            {
              id: userId,
              is_email_verified: true,
              email_verified: true,
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            }
          );
        } catch (e1) {
          try {
            await axios.put(
              `${baseUrl}/admin/editProfile`,
              {
                id: userId,
                is_email_verified: true,
                email_verified: true,
              },
              {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              }
            );
          } catch (e2) {}
        }

        // Update user state immediately
        setUserDetails((prev) => ({
          ...prev,
          email_verified: true,
          is_email_verified: true,
        }));

        setTimeout(() => {
          handleCloseEmailVerification();
        }, 1800);
      } else {
        const msg =
          response.data?.text || "Invalid verification code. Please try again.";
        setOtpError(msg);
        showToast(msg, { type: "error" });
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      const msg = "Error validating code. Please check your network.";
      setOtpError(msg);
      showToast(msg, { type: "error" });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleOtpBoxChange = (val, idx) => {
    const cleaned = val.replace(/[^0-9]/g, "");
    if (!cleaned) return;
    const newOtp = [...emailOtp];
    newOtp[idx] = cleaned[0];
    setEmailOtp(newOtp);
    if (idx < 3) {
      otpInputRefs.current[idx + 1]?.current?.focus();
    }
  };

  const handleOtpBoxKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newOtp = [...emailOtp];
      if (emailOtp[idx]) {
        newOtp[idx] = "";
        setEmailOtp(newOtp);
      } else if (idx > 0) {
        otpInputRefs.current[idx - 1]?.current?.focus();
        newOtp[idx - 1] = "";
        setEmailOtp(newOtp);
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, 4);
    if (pasted.length > 0) {
      const newOtp = ["", "", "", ""];
      for (let i = 0; i < pasted.length; i++) {
        newOtp[i] = pasted[i];
      }
      setEmailOtp(newOtp);
      const nextIdx = Math.min(pasted.length, 3);
      otpInputRefs.current[nextIdx]?.current?.focus();
    }
  };

  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const isSubscriptionActive = Boolean(
    subscriptionDetails?.subscriptionPlanActive
  );

  const memberSince = useMemo(() => {
    const dateStr = userDetails?.created_at || userDetails?.createdAt;
    if (!dateStr) return "Jan 2024";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "Jan 2024";
      return d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
    } catch {
      return "Jan 2024";
    }
  }, [userDetails]);

  const getDocumentUrl = (doc) => {
    if (!doc) return "";
    const rawUrl = doc.document_url || doc.file_url || doc.url || "";
    if (!rawUrl) return "";
    if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
      return rawUrl;
    }
    const cleanAsset = (
      assetUrl || "https://images.allhomedesire.com"
    ).replace(/\/+$/, "");
    const cleanPath = rawUrl.replace(/^\/+/, "");
    return `${cleanAsset}/${cleanPath}`;
  };

  const getDocTitle = (doc, index) => {
    if (!doc) return `Document #${index + 1}`;
    const rawName = doc.name || doc.document_name || doc.type || "";
    const normalized = normalizeDocKey
      ? normalizeDocKey(rawName)
      : rawName.toLowerCase();
    if (DOCUMENT_TITLES && DOCUMENT_TITLES[normalized]) {
      return DOCUMENT_TITLES[normalized];
    }
    if (rawName) {
      return rawName.charAt(0).toUpperCase() + rawName.slice(1);
    }
    return `Document #${index + 1}`;
  };

  // Primary document resolution for partner
  const primaryDoc = useMemo(() => {
    if (!documents || documents.length === 0) return null;
    const aadhaar = documents.find((d) =>
      (d.name || d.type || "").toLowerCase().includes("aadhar")
    );
    return aadhaar || documents[0];
  }, [documents]);

  // Secondary documents for partner
  const secondaryDocs = useMemo(() => {
    if (!documents || documents.length <= 1) return [];
    return documents.filter((d) => d !== primaryDoc);
  }, [documents, primaryDoc]);

  const hasKycDoc = Boolean(
    (documents && documents.length > 0) ||
    userDetails?.kyc_verified ||
    userDetails?.is_verified ||
    userDetails?.kyc_status === "APPROVED" ||
    userDetails?.documents?.length > 0
  );

  const isEmailVerified = Boolean(
    userDetails?.email_verified || userDetails?.is_email_verified
  );

  // Profile completion calculation:
  // For CUSTOMER: Customer Bio: 60 pts, Email Verified: 40 pts -> Total: 100 pts.
  // (KYC document requirement removed from customer profile)
  // For PARTNER: Full Name: 10 pts, Bio: 20 pts, KYC Doc: 30 pts, Email Verified: 40 pts -> Total: 100 pts.
  const profileCompletion = useMemo(() => {
    if (!isPartner) {
      let score = 0;
      if (formData.bio && formData.bio.trim().length > 0) {
        score += 60;
      }
      if (isEmailVerified) {
        score += 40;
      }
      return score;
    }

    let score = 0;
    if (formData.fullName && formData.fullName.trim().length > 0) {
      score += 10;
    }
    if (formData.bio && formData.bio.trim().length > 0) {
      score += 20;
    }
    if (hasKycDoc) {
      score += 30;
    }
    if (isEmailVerified) {
      score += 40;
    }
    return score;
  }, [isPartner, formData.fullName, formData.bio, hasKycDoc, isEmailVerified]);

  const initials = getInitials(formData.fullName || "Ayush Shrivastava");

  // Customer Loyalty Rewards calculation matching reference design
  const customerPoints = rewardPoints !== null ? Number(rewardPoints) : 2450;
  const customerTier = useMemo(() => {
    if (customerPoints >= 3000) {
      return {
        badge: "Platinum Loyalty Tier",
        tierRole: "CUSTOMER: PLATINUM TIER",
        targetTier: "Elite",
        ptsRemaining: 0,
        progressPct: 100,
      };
    }
    if (customerPoints >= 1000) {
      const ptsToGo = Math.max(0, 3000 - customerPoints);
      const progress =
        customerPoints === 2450
          ? 82
          : Math.round(((customerPoints - 1000) / 2000) * 100);
      return {
        badge: "Gold Loyalty Tier",
        tierRole: "CUSTOMER: GOLD TIER",
        targetTier: "Platinum",
        ptsRemaining: ptsToGo || 550,
        progressPct: progress,
      };
    }
    return {
      badge: "Silver Loyalty Tier",
      tierRole: "CUSTOMER: SILVER TIER",
      targetTier: "Gold",
      ptsRemaining: Math.max(0, 1000 - customerPoints),
      progressPct: Math.round((customerPoints / 1000) * 100),
    };
  }, [customerPoints]);

  const renderEmailVerificationModal = () => {
    if (!isEmailModalOpen) return null;

    const targetEmail =
      formData.email_address ||
      userDetails?.email_address ||
      "name@example.com";

    return (
      <div
        className="cupf__modal-backdrop"
        onClick={handleCloseEmailVerification}
      >
        <div
          className="cupf__verify-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cupf-verify-email-title"
        >
          <div className="cupf__verify-modal-header">
            <div className="cupf__verify-modal-header-left">
              <div className="cupf__verify-modal-icon-wrap">
                <Mail size={20} color="#2563eb" />
              </div>
              <div>
                <h3 id="cupf-verify-email-title">Email Verification</h3>
                <p>Verify your registered email address</p>
              </div>
            </div>
            <button
              type="button"
              className="cupf__verify-modal-close"
              onClick={handleCloseEmailVerification}
              aria-label="Close verification modal"
            >
              <X size={18} />
            </button>
          </div>

          <div className="cupf__verify-modal-body">
            {otpSuccess ? (
              <div className="cupf__verify-success-box">
                <div className="cupf__verify-success-icon-wrap">
                  <CheckCircle2 size={56} color="#059669" />
                </div>
                <h4>Email Verified!</h4>
                <p>
                  Your email <strong>{targetEmail}</strong> has been
                  successfully verified.
                </p>
              </div>
            ) : (
              <>
                <div className="cupf__verify-email-target">
                  <span className="cupf__verify-target-label">
                    Verifying email address:
                  </span>
                  <span className="cupf__verify-target-value">{targetEmail}</span>
                </div>

                {!otpSent ? (
                  <div className="cupf__verify-send-section">
                    <p className="cupf__verify-intro-text">
                      We will send a 4-digit verification code to your email inbox
                      to confirm your identity.
                    </p>
                    <button
                      type="button"
                      className="cupf__btn-send-otp"
                      onClick={handleSendEmailOtp}
                      disabled={isSendingOtp}
                    >
                      {isSendingOtp ? "Sending Code..." : "Send Verification Code"}
                    </button>
                  </div>
                ) : (
                  <div className="cupf__verify-otp-section">
                    <p className="cupf__verify-otp-instruction">
                      Enter the 4-digit verification code sent to your email:
                    </p>

                    {/* 4-Box OTP Input */}
                    <div
                      className="cupf__otp-input-row"
                      onPaste={handleOtpPaste}
                    >
                      {emailOtp.map((digit, idx) => (
                        <input
                          key={idx}
                          ref={otpInputRefs.current[idx]}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          value={digit}
                          className={`cupf__otp-box ${
                            digit ? "cupf__otp-box--filled" : ""
                          }`}
                          onChange={(e) =>
                            handleOtpBoxChange(e.target.value, idx)
                          }
                          onKeyDown={(e) => handleOtpBoxKeyDown(e, idx)}
                          disabled={isVerifyingOtp}
                          autoFocus={idx === 0}
                        />
                      ))}
                    </div>

                    {otpError && (
                      <div className="cupf__verify-error-msg">
                        <AlertTriangle size={14} />
                        <span>{otpError}</span>
                      </div>
                    )}

                    {/* Timer & Resend */}
                    <div className="cupf__verify-timer-row">
                      {otpTimer > 0 ? (
                        <span className="cupf__verify-timer-text">
                          Resend code in <strong>{formatTimer(otpTimer)}</strong>
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="cupf__btn-resend-otp"
                          onClick={handleSendEmailOtp}
                          disabled={isSendingOtp}
                        >
                          {isSendingOtp
                            ? "Sending..."
                            : "Didn't receive code? Resend OTP"}
                        </button>
                      )}
                    </div>

                    <button
                      type="button"
                      className="cupf__btn-verify-otp"
                      onClick={handleVerifyEmailOtp}
                      disabled={
                        emailOtp.join("").length !== 4 || isVerifyingOtp
                      }
                    >
                      {isVerifyingOtp ? "Verifying..." : "Validate & Confirm Email"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // CUSTOMER PROFILE & REWARDS DASHBOARD VIEW (Strictly for CUSTOMER role)
  // =========================================================================
  if (!isPartner) {
    return (
      <>
        <Notification />

        <div className="cupf cupf--customer">
          {/* Page Header */}
          <header className="cupf__header">
            <div className="cupf__header-left">
              <h1>Customer Profile &amp; Rewards</h1>
              <p>
                Manage your personal profile, track loyalty reward points, and
                redeem exclusive home benefits.
              </p>
            </div>
            <div className="cupf__header-actions">
              <button
                type="button"
                className="cupf__btn-share"
                onClick={handleShareProfile}
              >
                <Share2 size={16} />
                <span>Share Profile</span>
              </button>
              <button
                type="button"
                className="cupf__btn-edit-top"
                onClick={handleEditProfileHeaderClick}
              >
                <PencilLine size={16} />
                <span>Edit Profile</span>
              </button>
            </div>
          </header>

          {/* Two-Column Responsive Layout */}
          <main className="cupf__layout-grid">
            {/* ================= LEFT COLUMN ================= */}
            <div className="cupf__left-col">
              {/* 1. Personal Information Card */}
              <article className="cupf__card">
                <div className="cupf__card-header">
                  <h2 className="cupf__card-title">
                    <span className="cupf__title-dot" aria-hidden="true" />
                    <span>Personal Information</span>
                  </h2>
                  <div className="cupf__active-partner-badge cupf__verified-member-badge">
                    <span className="cupf__active-dot" aria-hidden="true" />
                    <span>Verified Member</span>
                  </div>
                </div>

                {/* Avatar & Customer Identity Row */}
                <div className="cupf__identity-row">
                  <div className="cupf__identity-left">
                    <div className="cupf__avatar-box">
                      {profilePicture ? (
                        <img
                          src={profilePicture}
                          alt={formData.fullName}
                          className="cupf__avatar-img"
                        />
                      ) : (
                        <span>{initials}</span>
                      )}
                      <label
                        htmlFor="cupf-avatar-upload-customer"
                        className="cupf__avatar-camera-btn"
                        title="Upload new profile picture"
                      >
                        <Camera size={13} />
                      </label>
                      <input
                        id="cupf-avatar-upload-customer"
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        className="cupf__visually-hidden"
                        onChange={handleProfilePictureChange}
                      />
                    </div>

                    <div className="cupf__identity-details">
                      <div className="cupf__identity-name-line">
                        <h3>{formData.fullName || "Ayush Shrivastava"}</h3>
                        <BadgeCheck
                          size={18}
                          className="cupf__verified-badge"
                          aria-label="Verified user"
                        />
                      </div>
                      <p className="cupf__identity-sub">
                        Valued Homebuyer • Member since {memberSince}
                      </p>
                    </div>
                  </div>

                  <div className="cupf__role-badge">
                    <span>{customerTier.tierRole}</span>
                  </div>
                </div>

                {/* Two-Column Form Field Grid */}
                <div className="cupf__form-grid">
                  {/* 1. Full Name */}
                  <div className="cupf__field-group">
                    <div className="cupf__field-label-row">
                      <label htmlFor="cupf-cust-field-fullname">Full Name</label>
                    </div>
                    <div className="cupf__input-wrapper">
                      <User size={16} className="cupf__input-icon" />
                      <input
                        id="cupf-cust-field-fullname"
                        ref={fullNameRef}
                        type="text"
                        className="cupf__input"
                        value={formData.fullName}
                        onChange={(e) =>
                          handleInputChange("fullName", e.target.value)
                        }
                        placeholder="Enter full name"
                      />
                      <button
                        type="button"
                        className="cupf__input-edit-btn"
                        onClick={() => fullNameRef.current?.focus()}
                        title="Edit Full Name"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  </div>

                  {/* 2. Email Address */}
                  <div className="cupf__field-group">
                    <div className="cupf__field-label-row">
                      <label htmlFor="cupf-cust-field-email">Email Address</label>
                      {isEmailVerified ? (
                        <span className="cupf__badge-verified-sm">
                          Verified
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="cupf__badge-pending-btn"
                          onClick={handleOpenEmailVerification}
                          title="Click to verify your email address"
                        >
                          <span className="cupf__badge-pending-sm">
                            Pending Verification
                          </span>
                          <span className="cupf__badge-verify-action">Verify</span>
                        </button>
                      )}
                    </div>
                    <div className="cupf__input-wrapper">
                      <Mail size={16} className="cupf__input-icon" />
                      <input
                        id="cupf-cust-field-email"
                        type="email"
                        className="cupf__input"
                        value={formData.email_address}
                        onChange={(e) =>
                          handleInputChange("email_address", e.target.value)
                        }
                        placeholder="name@example.com"
                      />
                    </div>
                  </div>

                  {/* 3. Account Type */}
                  <div className="cupf__field-group">
                    <div className="cupf__field-label-row">
                      <label htmlFor="cupf-cust-field-account-type">
                        Account Type
                      </label>
                    </div>
                    <div className="cupf__input-wrapper cupf__input-wrapper--readonly">
                      <Briefcase size={16} className="cupf__input-icon" />
                      <input
                        id="cupf-cust-field-account-type"
                        type="text"
                        readOnly
                        className="cupf__input cupf__input--readonly"
                        value="CUSTOMER"
                      />
                    </div>
                  </div>

                  {/* 4. Username */}
                  <div className="cupf__field-group">
                    <div className="cupf__field-label-row">
                      <label htmlFor="cupf-cust-field-username">Username</label>
                    </div>
                    <div className="cupf__input-wrapper">
                      <AtSign size={16} className="cupf__input-icon" />
                      <input
                        id="cupf-cust-field-username"
                        ref={usernameRef}
                        type="text"
                        className="cupf__input"
                        value={formData.user_name}
                        onChange={(e) =>
                          handleInputChange("user_name", e.target.value)
                        }
                        placeholder="username"
                      />
                      <button
                        type="button"
                        className="cupf__input-edit-btn"
                        onClick={() => usernameRef.current?.focus()}
                        title="Edit Username"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  </div>

                  {/* 5. WhatsApp / Contact Phone */}
                  <div className="cupf__field-group">
                    <div className="cupf__field-label-row">
                      <label htmlFor="cupf-cust-field-phone">
                        WhatsApp / Contact Phone
                      </label>
                    </div>
                    <div className="cupf__input-wrapper">
                      <FaWhatsapp
                        size={16}
                        className="cupf__input-icon cupf__input-icon--whatsapp"
                      />
                      <input
                        id="cupf-cust-field-phone"
                        ref={whatsappRef}
                        type="text"
                        className="cupf__input"
                        value={formData.whatsapp_mobile_number}
                        onChange={(e) =>
                          handleInputChange(
                            "whatsapp_mobile_number",
                            e.target.value
                          )
                        }
                        placeholder="+91 917408514482"
                      />
                      <button
                        type="button"
                        className="cupf__input-edit-btn"
                        onClick={() => whatsappRef.current?.focus()}
                        title="Edit WhatsApp / Phone"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  </div>

                  {/* 6. Location / City */}
                  <div className="cupf__field-group">
                    <div className="cupf__field-label-row">
                      <label htmlFor="cupf-cust-field-location">Location / City</label>
                    </div>
                    <div className="cupf__input-wrapper">
                      <MapPin size={16} className="cupf__input-icon" />
                      <input
                        id="cupf-cust-field-location"
                        ref={locationRef}
                        type="text"
                        className="cupf__input"
                        value={formData.location}
                        onChange={(e) =>
                          handleInputChange("location", e.target.value)
                        }
                        placeholder="Bhopal, Madhya Pradesh"
                      />
                      <button
                        type="button"
                        className="cupf__input-edit-btn"
                        onClick={() => locationRef.current?.focus()}
                        title="Edit Location"
                      >
                        <Pencil size={13} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* About You & Housing Preferences */}
                <div className="cupf__bio-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-cust-field-preferences">
                      About You &amp; Housing Preferences
                    </label>
                    <span className="cupf__char-count">
                      {formData.bio?.length || 0} / 300 characters
                    </span>
                  </div>
                  <div className="cupf__bio-wrapper">
                    <textarea
                      id="cupf-cust-field-preferences"
                      ref={bioRef}
                      rows={3}
                      maxLength={300}
                      className="cupf__bio-textarea"
                      value={formData.bio}
                      onChange={(e) => handleInputChange("bio", e.target.value)}
                      placeholder="Looking for premium residential properties and modern interior design solutions across Central India..."
                    />
                    <Pencil size={14} className="cupf__bio-pencil" />
                  </div>
                </div>

                {/* Form Action Buttons */}
                <div className="cupf__form-actions">
                  <button
                    type="button"
                    className="cupf__btn-discard"
                    onClick={handleDiscardChanges}
                    disabled={isSaving}
                  >
                    Discard Changes
                  </button>
                  <button
                    type="button"
                    className="cupf__btn-save"
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : "Save Profile Changes"}
                  </button>
                </div>
              </article>

              {/* 2. Profile Progress Card */}
              <article className="cupf__card cupf__progress-card">
                <div className="cupf__progress-header">
                  <h3 className="cupf__progress-title">
                    <Star
                      size={16}
                      fill="#F59E0B"
                      color="#F59E0B"
                      className="cupf__star-icon"
                    />
                    <span>Profile Progress</span>
                  </h3>
                  <div className="cupf__progress-score">
                    {profileCompletion}% Completed ({profileCompletion} / 100
                    Points)
                  </div>
                </div>

                <div className="cupf__progress-track">
                  <div
                    className="cupf__progress-fill"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>

                {/* Checklist Status Badges (Customer: Bio + Email verification) */}
                <div className="cupf__checklist-grid cupf__checklist-grid--customer">
                  {/* 1. Customer Profile Bio */}
                  {formData.bio && formData.bio.trim().length > 0 ? (
                    <div className="cupf__checklist-item cupf__checklist-item--success">
                      <span className="cupf__checklist-icon-wrap cupf__checklist-icon-wrap--success">
                        <Check size={11} strokeWidth={3.5} />
                      </span>
                      <span>Customer Profile Bio</span>
                    </div>
                  ) : (
                    <div
                      className="cupf__checklist-item cupf__checklist-item--warning cupf__checklist-item--interactive"
                      onClick={() => bioRef.current?.focus()}
                      title="Click to enter your housing preferences"
                    >
                      <span className="cupf__checklist-icon-wrap cupf__checklist-icon-wrap--warning">
                        <AlertTriangle size={13} strokeWidth={2.5} />
                      </span>
                      <span>Customer Profile Bio (+60 pts)</span>
                    </div>
                  )}

                  {/* 2. Email Verification */}
                  {isEmailVerified ? (
                    <div className="cupf__checklist-item cupf__checklist-item--success">
                      <span className="cupf__checklist-icon-wrap cupf__checklist-icon-wrap--success">
                        <Check size={11} strokeWidth={3.5} />
                      </span>
                      <span>Email Verified (+40 pts)</span>
                    </div>
                  ) : (
                    <div
                      className="cupf__checklist-item cupf__checklist-item--warning cupf__checklist-item--interactive"
                      title="Click to verify your email address"
                      onClick={handleOpenEmailVerification}
                    >
                      <span className="cupf__checklist-icon-wrap cupf__checklist-icon-wrap--warning">
                        <AlertTriangle size={13} strokeWidth={2.5} />
                      </span>
                      <span>Verify Email (+40 pts)</span>
                    </div>
                  )}
                </div>
              </article>
            </div>

            {/* ================= RIGHT COLUMN ================= */}
            <div className="cupf__right-col">
              {/* Customer Loyalty Rewards Card (Blue-to-Indigo Gradient) */}
              <article className="cupf__rewards-card">
                <div className="cupf__rewards-header">
                  <div className="cupf__rewards-tier-pill">
                    <Star size={12} fill="#F59E0B" color="#F59E0B" />
                    <span>{customerTier.badge}</span>
                  </div>
                  <div className="cupf__rewards-id">
                    ID: CUST-{userId || "8821"}
                  </div>
                </div>

                <div className="cupf__rewards-points-row">
                  <span className="cupf__rewards-points-number">
                    {customerPoints.toLocaleString()}
                  </span>
                  <span className="cupf__rewards-points-label">
                    DESIRE POINTS
                  </span>
                </div>

                <p className="cupf__rewards-desc">
                  Earn points on every property inquiry, verified visit, and home
                  improvement service.
                </p>

                <div className="cupf__rewards-progress-sec">
                  <div className="cupf__rewards-progress-header">
                    <span className="cupf__rewards-progress-title">
                      Tier Progress to {customerTier.targetTier} (
                      {customerTier.ptsRemaining} pts to go)
                    </span>
                    <span className="cupf__rewards-progress-pct">
                      {customerTier.progressPct}%
                    </span>
                  </div>
                  <div className="cupf__rewards-track">
                    <div
                      className="cupf__rewards-fill"
                      style={{ width: `${customerTier.progressPct}%` }}
                    />
                  </div>
                </div>

                <div className="cupf__rewards-footer">
                  <span className="cupf__rewards-expiring">
                    Expiring: None (Lifetime Active)
                  </span>
                  <Link to="/customer/rewards" className="cupf__btn-redeem">
                    Redeem Points
                  </Link>
                </div>
              </article>
            </div>
          </main>
        </div>

        {/* Email Verification Modal */}
        {renderEmailVerificationModal()}
      </>
    );
  }

  // =========================================================================
  // PARTNER PROFILE & ACCOUNT OVERVIEW VIEW (Strictly for PARTNER role)
  // =========================================================================
  return (
    <>
      <Notification />

      <div className="cupf cupf--partner">
        {/* Page Header */}
        <header className="cupf__header">
          <div className="cupf__header-left">
            <h1>Profile &amp; Account Overview</h1>
            <p>
              Manage your personal information, view verified compliance
              documents, and subscription status.
            </p>
          </div>
          <div className="cupf__header-actions">
            <button
              type="button"
              className="cupf__btn-share"
              onClick={handleShareProfile}
            >
              <Share2 size={16} />
              <span>Share Profile</span>
            </button>
            <button
              type="button"
              className="cupf__btn-edit-top"
              onClick={handleEditProfileHeaderClick}
            >
              <PencilLine size={16} />
              <span>Edit Profile</span>
            </button>
          </div>
        </header>

        {/* Two-Column Dashboard Layout */}
        <main className="cupf__layout-grid">
          {/* ================= LEFT COLUMN ================= */}
          <div className="cupf__left-col">
            {/* 1. Personal Information Card */}
            <article className="cupf__card">
              <div className="cupf__card-header">
                <h2 className="cupf__card-title">
                  <span className="cupf__title-dot" aria-hidden="true" />
                  <span>Personal Information</span>
                </h2>
                <div className="cupf__active-partner-badge">
                  <span className="cupf__active-dot" aria-hidden="true" />
                  <span>Active Partner</span>
                </div>
              </div>

              {/* Avatar & Identity Row */}
              <div className="cupf__identity-row">
                <div className="cupf__identity-left">
                  <div className="cupf__avatar-box">
                    {profilePicture ? (
                      <img
                        src={profilePicture}
                        alt={formData.fullName}
                        className="cupf__avatar-img"
                      />
                    ) : (
                      <span>{initials}</span>
                    )}
                    <label
                      htmlFor="cupf-avatar-upload-partner"
                      className="cupf__avatar-camera-btn"
                      title="Upload new profile picture"
                    >
                      <Camera size={13} />
                    </label>
                    <input
                      id="cupf-avatar-upload-partner"
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      className="cupf__visually-hidden"
                      onChange={handleProfilePictureChange}
                    />
                  </div>

                  <div className="cupf__identity-details">
                    <div className="cupf__identity-name-line">
                      <h3>{formData.fullName || "Ayush Shrivastava"}</h3>
                      <BadgeCheck
                        size={18}
                        className="cupf__verified-badge"
                        aria-label="Verified user"
                      />
                    </div>
                    <p className="cupf__identity-sub">
                      Partner &amp; Consultant • Member since {memberSince}
                    </p>
                  </div>
                </div>

                <div className="cupf__role-badge">
                  <span>ROLE: PARTNER</span>
                </div>
              </div>

              {/* Two-Column Form Field Grid */}
              <div className="cupf__form-grid">
                {/* 1. Full Name */}
                <div className="cupf__field-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-part-field-fullname">Full Name</label>
                  </div>
                  <div className="cupf__input-wrapper">
                    <User size={16} className="cupf__input-icon" />
                    <input
                      id="cupf-part-field-fullname"
                      ref={fullNameRef}
                      type="text"
                      className="cupf__input"
                      value={formData.fullName}
                      onChange={(e) =>
                        handleInputChange("fullName", e.target.value)
                      }
                      placeholder="Enter full name"
                    />
                    <button
                      type="button"
                      className="cupf__input-edit-btn"
                      onClick={() => fullNameRef.current?.focus()}
                      title="Edit Full Name"
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                </div>

                {/* 2. Email Address */}
                <div className="cupf__field-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-part-field-email">Email Address</label>
                    {isEmailVerified ? (
                      <span className="cupf__badge-verified-sm">
                        Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="cupf__badge-pending-btn"
                        onClick={handleOpenEmailVerification}
                        title="Click to verify your email address"
                      >
                        <span className="cupf__badge-pending-sm">
                          Pending Verification
                        </span>
                        <span className="cupf__badge-verify-action">Verify</span>
                      </button>
                    )}
                  </div>
                  <div className="cupf__input-wrapper">
                    <Mail size={16} className="cupf__input-icon" />
                    <input
                      id="cupf-part-field-email"
                      type="email"
                      className="cupf__input"
                      value={formData.email_address}
                      onChange={(e) =>
                        handleInputChange("email_address", e.target.value)
                      }
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                {/* 3. Account Type */}
                <div className="cupf__field-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-part-field-account-type">
                      Account Type
                    </label>
                  </div>
                  <div className="cupf__input-wrapper cupf__input-wrapper--readonly">
                    <Briefcase size={16} className="cupf__input-icon" />
                    <input
                      id="cupf-part-field-account-type"
                      type="text"
                      readOnly
                      className="cupf__input cupf__input--readonly"
                      value="PARTNER"
                    />
                  </div>
                </div>

                {/* 4. Username */}
                <div className="cupf__field-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-part-field-username">Username</label>
                  </div>
                  <div className="cupf__input-wrapper">
                    <AtSign size={16} className="cupf__input-icon" />
                    <input
                      id="cupf-part-field-username"
                      ref={usernameRef}
                      type="text"
                      className="cupf__input"
                      value={formData.user_name}
                      onChange={(e) =>
                        handleInputChange("user_name", e.target.value)
                      }
                      placeholder="username"
                    />
                    <button
                      type="button"
                      className="cupf__input-edit-btn"
                      onClick={() => usernameRef.current?.focus()}
                      title="Edit Username"
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                </div>

                {/* 5. WhatsApp / Contact Phone */}
                <div className="cupf__field-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-part-field-phone">
                      WhatsApp / Contact Phone
                    </label>
                  </div>
                  <div className="cupf__input-wrapper">
                    <FaWhatsapp
                      size={16}
                      className="cupf__input-icon cupf__input-icon--whatsapp"
                    />
                    <input
                      id="cupf-part-field-phone"
                      ref={whatsappRef}
                      type="text"
                      className="cupf__input"
                      value={formData.whatsapp_mobile_number}
                      onChange={(e) =>
                        handleInputChange(
                          "whatsapp_mobile_number",
                          e.target.value
                        )
                      }
                      placeholder="+91 917408514482"
                    />
                    <button
                      type="button"
                      className="cupf__input-edit-btn"
                      onClick={() => whatsappRef.current?.focus()}
                      title="Edit WhatsApp / Phone"
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                </div>

                {/* 6. Location / City */}
                <div className="cupf__field-group">
                  <div className="cupf__field-label-row">
                    <label htmlFor="cupf-part-field-location">Location / City</label>
                  </div>
                  <div className="cupf__input-wrapper">
                    <MapPin size={16} className="cupf__input-icon" />
                    <input
                      id="cupf-part-field-location"
                      ref={locationRef}
                      type="text"
                      className="cupf__input"
                      value={formData.location}
                      onChange={(e) =>
                        handleInputChange("location", e.target.value)
                      }
                      placeholder="Bhopal, Madhya Pradesh"
                    />
                    <button
                      type="button"
                      className="cupf__input-edit-btn"
                      onClick={() => locationRef.current?.focus()}
                      title="Edit Location"
                    >
                      <Pencil size={13} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Full Width: Professional Bio */}
              <div className="cupf__bio-group">
                <div className="cupf__field-label-row">
                  <label htmlFor="cupf-part-field-bio">Professional Bio</label>
                  <span className="cupf__char-count">
                    {formData.bio?.length || 0} / 300 characters
                  </span>
                </div>
                <div className="cupf__bio-wrapper">
                  <textarea
                    id="cupf-part-field-bio"
                    ref={bioRef}
                    rows={3}
                    maxLength={300}
                    className="cupf__bio-textarea"
                    value={formData.bio}
                    onChange={(e) => handleInputChange("bio", e.target.value)}
                    placeholder="Tell clients about your professional experience and real estate services..."
                  />
                  <Pencil size={14} className="cupf__bio-pencil" />
                </div>
              </div>

              {/* Form Action Buttons */}
              <div className="cupf__form-actions">
                <button
                  type="button"
                  className="cupf__btn-discard"
                  onClick={handleDiscardChanges}
                  disabled={isSaving}
                >
                  Discard Changes
                </button>
                <button
                  type="button"
                  className="cupf__btn-save"
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                >
                  {isSaving ? "Saving..." : "Save Profile Changes"}
                </button>
              </div>
            </article>

            {/* 2. Profile Progress Card */}
            <article className="cupf__card cupf__progress-card">
              <div className="cupf__progress-header">
                <h3 className="cupf__progress-title">
                  <Star
                    size={16}
                    fill="#F59E0B"
                    color="#F59E0B"
                    className="cupf__star-icon"
                  />
                  <span>Profile Progress</span>
                </h3>
                <div className="cupf__progress-score">
                  {profileCompletion}% Completed ({profileCompletion} / 100
                  Points)
                </div>
              </div>

              <div className="cupf__progress-track">
                <div
                  className="cupf__progress-fill"
                  style={{ width: `${profileCompletion}%` }}
                />
              </div>

              {/* 3 Checklist / Status Badges */}
              <div className="cupf__checklist-grid">
                {/* 1. KYC Document Uploaded */}
                {documents && documents.length > 0 ? (
                  <div className="cupf__checklist-item cupf__checklist-item--success">
                    <CheckCircle2 size={16} color="#059669" />
                    <span>KYC Document Uploaded</span>
                  </div>
                ) : (
                  <Link
                    to="/partner/documents"
                    className="cupf__checklist-item cupf__checklist-item--warning"
                  >
                    <AlertTriangle size={15} color="#D97706" />
                    <span>KYC Document Pending (+30 pts)</span>
                  </Link>
                )}

                {/* 2. Partner Profile Bio */}
                {formData.bio && formData.bio.trim().length > 0 ? (
                  <div className="cupf__checklist-item cupf__checklist-item--success">
                    <CheckCircle2 size={16} color="#059669" />
                    <span>Partner Profile Bio</span>
                  </div>
                ) : (
                  <div
                    className="cupf__checklist-item cupf__checklist-item--warning"
                    onClick={() => bioRef.current?.focus()}
                  >
                    <AlertTriangle size={15} color="#D97706" />
                    <span>Add Profile Bio (+20 pts)</span>
                  </div>
                )}

                {/* 3. Email Verification */}
                {isEmailVerified ? (
                  <div className="cupf__checklist-item cupf__checklist-item--success">
                    <CheckCircle2 size={16} color="#059669" />
                    <span>Email Verified (+40 pts)</span>
                  </div>
                ) : (
                  <div
                    className="cupf__checklist-item cupf__checklist-item--warning cupf__checklist-item--interactive"
                    title="Click to verify your email address"
                    onClick={handleOpenEmailVerification}
                  >
                    <AlertTriangle size={15} color="#D97706" />
                    <span>Verify Email (+40 pts)</span>
                  </div>
                )}
              </div>
            </article>
          </div>

          {/* ================= RIGHT COLUMN ================= */}
          <div className="cupf__right-col">
            {/* 1. Membership Tier Card (Dark Navy) */}
            <article className="cupf__membership-card">
              <div className="cupf__tier-header">
                <div className="cupf__tier-pill">
                  <Star size={12} fill="#F59E0B" color="#F59E0B" />
                  <span>Membership Tier</span>
                </div>
                <div className="cupf__tier-id">
                  ID: AHD-P-{userId || "8821"}
                </div>
              </div>

              <h3 className="cupf__tier-title">
                {subscriptionDetails?.planName || "Partner Basic Tier"}
              </h3>
              <p className="cupf__tier-desc">
                Upgrade to Premium Partner to unlock unlimited customer leads,
                featured listings, and dedicated CRM tools.
              </p>

              <div className="cupf__tier-features">
                <div className="cupf__tier-feature-row">
                  <div className="cupf__tier-feature-left">
                    <Check size={14} color="#10B981" />
                    <span>Standard Property Listings</span>
                  </div>
                  <span className="cupf__tier-feature-val cupf__tier-feature-val--included">
                    Included
                  </span>
                </div>

                <div className="cupf__tier-feature-row">
                  <div className="cupf__tier-feature-left">
                    <Check size={14} color="#10B981" />
                    <span>Verified KYC Identity Badge</span>
                  </div>
                  <span className="cupf__tier-feature-val cupf__tier-feature-val--active">
                    Active
                  </span>
                </div>

                <div className="cupf__tier-feature-row">
                  <div className="cupf__tier-feature-left">
                    <X size={14} color="#EF4444" />
                    <span>Direct Priority Care Leads</span>
                  </div>
                  <span className="cupf__tier-feature-val cupf__tier-feature-val--pro">
                    Pro Only
                  </span>
                </div>
              </div>

              <div className="cupf__tier-footer">
                <div className="cupf__tier-status-col">
                  <span className="cupf__tier-status-lbl">CURRENT STATUS</span>
                  <span
                    className={`cupf__tier-status-val ${
                      isSubscriptionActive
                        ? "cupf__tier-status-val--active"
                        : "cupf__tier-status-val--inactive"
                    }`}
                  >
                    {isSubscriptionActive
                      ? `Active Plan (${subscriptionDetails?.durationInDays || 30} Days left)`
                      : "Inactive Plan (0 Days left)"}
                  </span>
                </div>

                <Link to="/partner/subscription" className="cupf__btn-upgrade">
                  Upgrade Subscription
                </Link>
              </div>
            </article>

            {/* 2. Uploaded Documents Card */}
            <article className="cupf__documents-card">
              <div className="cupf__docs-card-header">
                <div className="cupf__docs-card-header-left">
                  <div className="cupf__docs-icon-box">
                    <FileText size={18} color="#2563EB" />
                  </div>
                  <div className="cupf__docs-title-box">
                    <h3>Uploaded Documents</h3>
                    <p>Verified KYC records &amp; certifications (View Only)</p>
                  </div>
                </div>

                <div className="cupf__locked-badge">
                  <Lock size={12} color="#64748B" />
                  <span>Locked / Read-Only</span>
                </div>
              </div>

              {/* Primary Document (Aadhaar Card) */}
              {primaryDoc ? (
                (() => {
                  const title = getDocTitle(primaryDoc, 0);
                  const fileUrl = getDocumentUrl(primaryDoc);
                  const isPdf =
                    fileUrl.toLowerCase().endsWith(".pdf") ||
                    (primaryDoc.name || "").toLowerCase().includes("pdf");

                  return (
                    <div className="cupf__primary-doc-card">
                      <div className="cupf__doc-header-row">
                        <div className="cupf__doc-meta-col">
                          <div className="cupf__doc-icon-sq">
                            <CreditCard size={17} color="#2563EB" />
                          </div>
                          <div>
                            <div className="cupf__doc-heading">
                              <h4 className="cupf__doc-name">{title}</h4>
                              <span className="cupf__doc-verified-badge">
                                <Check size={10} /> Verified
                              </span>
                            </div>
                            <p className="cupf__doc-subline">
                              Government ID Verification • Aadhaar ****{" "}
                              {String(userId).slice(-4) || "4482"}
                            </p>
                          </div>
                        </div>

                        <span className="cupf__doc-authority-tag">UIDAI</span>
                      </div>

                      {/* Branded Digital Verification Box */}
                      <div className="cupf__digital-preview-box">
                        <ShieldCheck size={28} color="#2563EB" />
                        <div className="cupf__cert-brand">AgentWorks</div>
                        <div className="cupf__cert-sub">
                          CERTIFIED KYC PARTNER
                        </div>
                        <div className="cupf__cert-msg">
                          Digital Aadhaar Verification Authenticated
                        </div>
                        <div className="cupf__cert-uid">
                          UID-AUTH-2024-{String(userId || "917488").padStart(6, "0")}
                        </div>
                      </div>

                      {/* Action buttons (50% / 50% flex width) */}
                      <div className="cupf__doc-btn-group">
                        <button
                          type="button"
                          className="cupf__doc-action-btn cupf__doc-action-btn--primary"
                          onClick={() =>
                            setViewingDoc({
                              title,
                              url: fileUrl || "https://images.allhomedesire.com/documents/sample_kyc.pdf",
                              isPdf,
                            })
                          }
                        >
                          <Eye size={14} />
                          <span>View</span>
                        </button>
                        <a
                          href={fileUrl || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cupf__doc-action-btn cupf__doc-action-btn--outline"
                          onClick={(e) => {
                            if (!fileUrl) {
                              e.preventDefault();
                              showToast("Preview file in full window.", {
                                type: "info",
                              });
                            }
                          }}
                        >
                          <ExternalLink size={14} />
                          <span>Open File</span>
                        </a>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="cupf__primary-doc-card">
                  <div className="cupf__doc-header-row">
                    <div className="cupf__doc-meta-col">
                      <div className="cupf__doc-icon-sq">
                        <CreditCard size={17} color="#2563EB" />
                      </div>
                      <div>
                        <div className="cupf__doc-heading">
                          <h4 className="cupf__doc-name">Aadhaar Card</h4>
                          <span className="cupf__doc-verified-badge">
                            <Check size={10} /> Verified
                          </span>
                        </div>
                        <p className="cupf__doc-subline">
                          Government ID Verification • Aadhaar **** 4482
                        </p>
                      </div>
                    </div>
                    <span className="cupf__doc-authority-tag">UIDAI</span>
                  </div>

                  <div className="cupf__digital-preview-box">
                    <ShieldCheck size={28} color="#2563EB" />
                    <div className="cupf__cert-brand">AgentWorks</div>
                    <div className="cupf__cert-sub">CERTIFIED KYC PARTNER</div>
                    <div className="cupf__cert-msg">
                      Digital Aadhaar Verification Authenticated
                    </div>
                    <div className="cupf__cert-uid">
                      UID-AUTH-2024-917488
                    </div>
                  </div>

                  <div className="cupf__doc-btn-group">
                    <Link
                      to="/partner/documents"
                      className="cupf__doc-action-btn cupf__doc-action-btn--primary"
                    >
                      <Eye size={14} />
                      <span>View</span>
                    </Link>
                    <Link
                      to="/partner/documents"
                      className="cupf__doc-action-btn cupf__doc-action-btn--outline"
                    >
                      <ExternalLink size={14} />
                      <span>Open File</span>
                    </Link>
                  </div>
                </div>
              )}

              {/* Secondary Document: Partner Agreement Certificate */}
              {secondaryDocs.length > 0 ? (
                secondaryDocs.map((secDoc, idx) => {
                  const secTitle = getDocTitle(secDoc, idx + 1);
                  const secUrl = getDocumentUrl(secDoc);
                  const isPdf =
                    secUrl.toLowerCase().endsWith(".pdf") ||
                    (secDoc.name || "").toLowerCase().includes("pdf");

                  return (
                    <div
                      key={secDoc.id || idx}
                      className="cupf__secondary-doc-card"
                    >
                      <div className="cupf__secondary-doc-left">
                        <div className="cupf__secondary-doc-icon">
                          <FileText size={17} color="#2563EB" />
                        </div>
                        <div>
                          <h4 className="cupf__secondary-doc-title">
                            {secTitle}
                          </h4>
                          <p className="cupf__secondary-doc-sub">
                            Verified Document • {isPdf ? "PDF" : "Image"}
                          </p>
                        </div>
                      </div>

                      {secUrl && (
                        <button
                          type="button"
                          className="cupf__secondary-doc-view-btn"
                          onClick={() =>
                            setViewingDoc({
                              title: secTitle,
                              url: secUrl,
                              isPdf,
                            })
                          }
                        >
                          View
                        </button>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="cupf__secondary-doc-card">
                  <div className="cupf__secondary-doc-left">
                    <div className="cupf__secondary-doc-icon">
                      <FileText size={17} color="#2563EB" />
                    </div>
                    <div>
                      <h4 className="cupf__secondary-doc-title">
                        Partner Agreement Certificate
                      </h4>
                      <p className="cupf__secondary-doc-sub">
                        Signed on 15 Jan 2024 • PDF (1.2 MB)
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="cupf__secondary-doc-view-btn"
                    onClick={() =>
                      showToast(
                        "Partner Agreement Certificate authenticated.",
                        { type: "info" }
                      )
                    }
                  >
                    View
                  </button>
                </div>
              )}

              {/* Security Footer Notice */}
              <div className="cupf__docs-security-note">
                <Info size={14} color="#64748B" />
                <span>
                  Documents are archived in tamper-proof encrypted cloud
                  storage. Transparent identity verification.
                </span>
              </div>
            </article>
          </div>
        </main>
      </div>

      {/* Document Preview Modal */}
      {viewingDoc && (
        <div
          className="cupf__preview-modal-backdrop"
          onClick={() => setViewingDoc(null)}
        >
          <div
            className="cupf__preview-modal-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cupf__preview-modal-header">
              <span className="fw-bold text-dark">{viewingDoc.title}</span>
              <div className="d-flex align-items-center gap-2">
                {viewingDoc.url && (
                  <a
                    href={viewingDoc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                  >
                    <ExternalLink size={14} />
                    <span>Open Full Screen</span>
                  </a>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1"
                  onClick={() => setViewingDoc(null)}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="cupf__preview-modal-body">
              {viewingDoc.isPdf ||
              viewingDoc.url.toLowerCase().endsWith(".pdf") ? (
                <iframe
                  src={`${viewingDoc.url}#toolbar=0`}
                  title={viewingDoc.title}
                  className="cupf__preview-modal-iframe"
                />
              ) : (
                <img
                  src={viewingDoc.url}
                  alt={viewingDoc.title}
                  className="cupf__preview-modal-img"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Modal */}
      {renderEmailVerificationModal()}
    </>
  );
};

const getInitials = (name) => {
  const trimmedName = name?.trim();
  if (!trimmedName) {
    return "AS";
  }
  const segments = trimmedName.split(/\s+/).filter(Boolean);
  if (segments.length === 1) {
    return segments[0].slice(0, 2).toUpperCase();
  }
  return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
};

export default Profile;
